import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Search, Clock, CheckCircle, ScanLine, Monitor, Trash2, BookOpen, Printer, ChevronLeft, Code, Sparkles } from 'lucide-react';
import { useBanqueQuestions } from '../../hooks/fetch_api_hook/useBanqueQuestions';
import { useQuestionsAttendusAutresNiveaux } from '../../hooks/fetch_api_hook/useQuestionsAttendusAutresNiveaux';
import { fusionnerQuestions } from '../../utils/banqueQuestions';
import { useSessionsQCM } from '../../hooks/fetch_api_hook/useSessionsQCM';
import { useCartesAruco } from '../../hooks/fetch_api_hook/useCartesAruco';
import { useBilanCible } from '../../hooks/fetch_api_hook/useBilanCible';
import { useAttendusAvecArbre, prerequisActifs } from '../../hooks/fetch_api_hook/useAttendusAvecArbre';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { usePutApi } from '../../hooks/put_api_hook/usePutApi';
import { useUser } from '../../context/UserContext';
import { useClasseContext } from '../../context/ClasseContext';
import useConfirm from '../../hooks/ui/useConfirm';
import axios from '../../interceptors/axios';
import { url } from '../../utils/confserveur';
import PanelAttendus from '../../components/VueQuestion/PanelAttendus';
import AttenduExercices from '../../components/VueQuestion/AttenduExercices';
import BandeauAttendu from '../../components/VueQuestion/BandeauAttendu';
import GroupesBesoin from '../../components/VueQuestion/GroupesBesoin';
import { apercuTexte, formatJourMois, pluriel, texteLisible } from '../../components/VueQuestion/formatConfig';
import ApercuExercice from '../../components/VueQuestion/ApercuExercice';
import ApercuEleveModal from '../../components/VueQuestion/ApercuEleveModal';
import QuestionForm from '../../components/VueQuestion/QuestionForm';
import { getQuestionTypeLabel } from '../../utils/questionUtils';
import LaunchModal from '../../components/VueQuestion/LaunchModal';
import MathText from '../../components/VueQuestion/MathText';
import PastilleNiveau, { horsNiveau } from '../../components/CollerNotes/PastilleNiveau';
import IconesModes from '../../components/VueQuestion/IconesModes';
import PanierGroupes, { ActionsLigne, MarqueProposee } from '../../components/VueQuestion/PanierGroupes';
import LigneDiagnostic from '../../components/VueQuestion/LigneDiagnostic';
import { adapterPourCartes, sansVersionQcm } from '../../utils/adaptationCartes';
import { autreQuestion, composerDiagnostic, composerGroupes, decouperPapier, etatBandeau, exercicesDuGroupe, groupesADiffuser, manqueTotal, ordonnerParNiveau, placerGroupes } from '../../utils/besoins';
import { autresClassesDuNiveau, autresGroupesDuNiveau, regenererGeneres } from '../../utils/dupliquerSelection';
import { estDemiGroupe } from '../../utils/groupesDeBesoin';
import { hasFullAccess } from '../../utils/permissions';
import { hasGenerator, getCategories, generateExercice } from '../../generators';

const NIVEAU_MAP = { '6': '6eme', '5': '5eme', '4': '4eme', '3': '3eme' };
const GENERATEURS = { hasGenerator, getCategories, generateExercice };
const MOTIF_CARTES_GROUPES = 'Cartes QR : toute la classe seulement';
// F6 : comment parler de la session en cours, selon son intention.
const SESSIONS = {
  evaluation: { garder: 'Garder mon évaluation' },
  diagnostic: { garder: 'Garder mon diagnostic' },
  groupes: { garder: 'Garder mes groupes' },
};
// Identifiant d'un exercice propre à un groupe (clé de liste, retrait).
let dernierIdPropre = 0;
// Exercice propre à un groupe : rangé comme au panier, avec son identifiant
// (clé de liste, retrait) et sa clé d'exerciseur (regroupement à l'impression).
// Formats de lancement : classe vide, papier seul ; groupes, en ligne
// (une séance par groupe) ou papier, jamais les cartes (toute la classe).
const FORMATS_CLASSE_VIDE = ['papier'];
const FORMATS_GROUPES = ['en_ligne', 'papier'];
const AUCUNE_CLASSE = [];
const exercicePropre = (attenduId, cle, payload, infos) => {
  const id = `propre_${++dernierIdPropre}`;
  const p = versPanier(attenduId, payload, infos);
  return { id, attenduId, cle, payload: { ...p, _tmpId: id, _meta: { ...p._meta, exerciseurKey: cle } } };
};
// Libellé, niveau et rubrique des attendus d'un bilan, par identifiant.
const infosDuBilan = (bilan) => Object.fromEntries((bilan.attendus || []).map((a) => [a.attendu_id, {
  id: a.attendu_id, description: a.description, niveau: a.niveau, rubrique: a.rubrique,
}]));
// L'attendu de la séance puis ses prérequis bloquants (le plus proche
// d'abord) : raccourcis du panneau des attendus pendant la séance.
const parcoursDuBilan = (bilan, cible, infos) => [
  { ...infos[cible.id], ...cible },
  ...(bilan.descente || []).filter((id) => id !== cible.id).map((id) => infos[id]).filter(Boolean),
];
// Un exercice composé entre au panier rattaché à son attendu. `compoAuto:
// false` : un « Composer N » lancé ensuite sur un attendu ouvert ne le
// remplace pas (il compte comme un choix déjà fait) ; la marque ✦ suit
// `origine: 'auto'`.
const versPanier = (attenduId, payload, infos) => ({
  ...payload,
  attendu: payload.attendu ?? attenduId,
  _meta: {
    ...payload._meta,
    compoAuto: false,
    ...(infos[attenduId] ? { attenduTitre: infos[attenduId].description } : {}),
  },
});

// ↻ Autre question (tiroir du diagnostic) : la question remplacée garde sa
// place dans le panier, donc dans la passation, même si sa clé change.
const remplacerDansPanier = (panier, cle, ancien, cleNouvelle, nouveau) => {
  const next = new Map();
  panier.forEach((entree, k) => {
    if (k === cle && cleNouvelle === cle) {
      next.set(k, { ...entree, payloads: entree.payloads.map((p) => (p === ancien ? nouveau : p)) });
    } else if (k === cle) {
      // Autre carte : la nouvelle clé prend la place de l'ancienne (fusionnée si elle existait).
      next.set(cleNouvelle, { payloads: [nouveau, ...(panier.get(cleNouvelle)?.payloads || [])] });
      const reste = entree.payloads.filter((p) => p !== ancien);
      if (reste.length > 0) next.set(k, { ...entree, payloads: reste });
    } else if (k !== cleNouvelle) {
      next.set(k, entree);
    }
  });
  return next;
};

// Attendu RETENU par classe entre deux visites (localStorage, jamais le
// backend — décision 2026-09-02) : au retour, ses exercices s'affichent
// directement et le panneau s'ouvre déplié dessus. Le logout vide le
// localStorage (ClasseContext) : la mémoire ne survit pas à une déconnexion,
// limite connue et assumée. Le niveau est stocké avec l'attendu pour ignorer
// une mémoire qui ne correspondrait plus à la classe. Un attendu d'un AUTRE
// niveau que la classe n'est pas rouvert (décision prof 2026-09-29) : la
// composition repart vide, panneau sur le niveau de la classe, pour ne pas
// rester sur la 6e dans une 4e sans s'en rendre compte.
const cleAttenduRetenu = (idclasse) => `qcm.attendu.${idclasse}`;
const lireAttenduRetenu = (idclasse, niveauClasse) => {
  try {
    const v = JSON.parse(localStorage.getItem(cleAttenduRetenu(idclasse)) || 'null');
    if (!v || !v.id) return null;
    if (v.niveau && niveauClasse && v.niveau !== niveauClasse) return null;
    return v;
  } catch {
    return null;
  }
};
const ecrireAttenduRetenu = (idclasse, attendu) => {
  try {
    localStorage.setItem(cleAttenduRetenu(idclasse), JSON.stringify(attendu));
  } catch {
    // stockage indisponible : on vit sans mémoire
  }
};

// Nettoie un exercice généré pour banque-questions/bulk-create : la
// destructuration ne conserve que les champs du serializer BanqueQuestion
// (id/_tmpId, purement front, tombent d'eux-mêmes).
// `generator_key` = provenance exerciseur (_meta.generatorKey) — le couple
// (attendu, generator_key) rattache l'instance à sa carte/son format pour la
// réutilisation. Tant que le back n'a pas le champ, il est ignoré (no-op).
const toBanquePayload = ({ type, texte, contenu, elements_additionnels, niveau, matiere, attendu, _meta }) =>
  ({ type, texte, contenu, elements_additionnels, niveau, matiere, attendu, generator_key: _meta?.generatorKey || null });

const QCMDashboard = () => {
  const { idclasse } = useParams();
  const navigate = useNavigate();
  const { user, selectedEtablissementId, etablissementData } = useUser();
  const { classeNom, groupNom, classeDetail } = useClasseContext();

  // « Donner aussi à » : les autres classes du même niveau où le prof a un
  // accès complet (données déjà en mémoire, aucune requête). Pour un groupe
  // (décision prof 2026-09-30) : ses autres groupes du même type et du même
  // niveau, demi-groupes entre eux, groupes de besoin entre eux. Avancement
  // affiché dans la modale pendant les créations en série.
  const groupeCourant = useMemo(
    () => (groupNom ? etablissementData?.groupes?.find((g) => String(g.id) === String(idclasse)) : null),
    [groupNom, etablissementData, idclasse],
  );
  const autresClasses = useMemo(() => {
    if (classeNom) {
      return autresClassesDuNiveau(etablissementData?.classes, idclasse, classeNom, (id) => hasFullAccess(id, user, etablissementData));
    }
    const professeur = etablissementData?.professeurs?.find((p) => p.id === user?.id);
    return autresGroupesDuNiveau(etablissementData?.groupes, professeur, groupeCourant);
  }, [etablissementData, idclasse, classeNom, user, groupeCourant]);
  const typeCibles = classeNom ? 'classe' : estDemiGroupe(groupeCourant) ? 'demiGroupe' : 'groupeDeBesoin';
  const [avancementClasses, setAvancementClasses] = useState(null);

  // « Mode revue » (allègement 2026-09-02) : les numéros d'attendu et les clés
  // de format sont des informations de crible (repérer un générateur, citer
  // une carte sans erreur), pas de prof. Cachées par défaut, un interrupteur
  // dans l'en-tête les fait réapparaître ; choix retenu localement, pour tout
  // le navigateur (pas par classe).
  const [modeRevue, setModeRevue] = useState(() => {
    try { return localStorage.getItem('qcm.modeRevue') === '1'; } catch { return false; }
  });
  const basculerModeRevue = useCallback(() => {
    setModeRevue((prev) => {
      const next = !prev;
      try { localStorage.setItem('qcm.modeRevue', next ? '1' : '0'); } catch { /* sans mémoire */ }
      return next;
    });
  }, []);

  // Cette page n'a plus qu'une vue : la composition de session. L'onglet
  // « Cartes ArUco » a disparu — les cartes sont préparées automatiquement à la
  // création de la session et les cas particuliers se règlent en séance.
  const [refreshKey, setRefreshKey] = useState(0);

  // Sélection de questions pour lancer un QCM
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Exercices générés sélectionnés : Map<"attenduId_categoryId" → payload>
  const [selectedExercices, setSelectedExercices] = useState(() => new Map());
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [launchMode, setLaunchMode] = useState(null); // 'aruco' | 'en_ligne' | 'papier'
  const [launchLoading, setLaunchLoading] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);

  // Panel attendus + sélection d'attendu. Le panneau est OUVERT à l'arrivée
  // (rien à composer tant qu'aucun attendu n'est choisi : le clic « Parcourir
  // les attendus » ne faisait que l'ouvrir) et se REFERME à la sélection, pour
  // laisser voir les exercices choisis — avant, il restait ouvert par-dessus
  // et il fallait le fermer à la main (chantier composition, 2026-09-02).
  // Attendu retenu pour cette classe (cf. lireAttenduRetenu) : s'il existe, on
  // arrive directement sur ses exercices, panneau fermé (sauf attendu d'un
  // autre niveau que la classe : composition vide, 2026-09-29).
  const niveauClasseInitial = NIVEAU_MAP[(classeNom || groupNom || '').charAt(0)] || '';
  const [selectedAttendu, setSelectedAttendu] = useState(() => lireAttenduRetenu(idclasse, niveauClasseInitial));
  const [panelOpen, setPanelOpen] = useState(() => !lireAttenduRetenu(idclasse, niveauClasseInitial));

  // Intention de la session (chantier composer-evaluation, R2, décision du
  // 2026-10-02) : un champ de l'état du panier, jamais en base. `null` =
  // évaluation, le fonctionnement d'aujourd'hui ; sinon `{ type: 'diagnostic',
  // cible, infos, ignores }` (`infos` : libellé et niveau de chaque prérequis
  // du diagnostic, pour les lignes du panier) ou `{ type: 'groupes', base,
  // cible, date, manque, noms, sansResultat, groupes }` : les exercices de
  // l'attendu, communs aux 3 groupes (R8), sont le panier ; ceux propres à un
  // groupe vivent dans `groupes[].propres`. Retombe à `null` quand le panier
  // (communs et propres) se vide.
  const [intention, setIntention] = useState(null);
  // R11 : des groupes ne se diffusent pas en cartes (toute la classe) ; en
  // ligne, une séance par groupe, papier, un sujet par groupe.
  const enGroupes = intention?.type === 'groupes';
  const enDiagnostic = intention?.type === 'diagnostic';
  // Séances de groupes déjà créées en ligne (F7) : oubliées quand la session
  // groupes prend fin ou qu'une autre est préparée.
  const diffusionRef = useRef(null);
  useEffect(() => { if (!enGroupes) diffusionRef.current = null; }, [enGroupes]);
  // « ouvrir » un prérequis (bandeau, panier) : l'attendu où revenir.
  const [attenduRetour, setAttenduRetour] = useState(null);

  // Détail des formats sélectionnés pour la barre de statut
  const formatDetail = useMemo(() => {
    const byType = {};
    let total = 0;
    selectedExercices.forEach(({ payloads }) => {
      const count = payloads.length;
      const payload = payloads[0];
      if (!payload) return;
      const label = getQuestionTypeLabel(payload);
      byType[label] = (byType[label] || 0) + count;
      total += count;
    });
    const detail = Object.entries(byType).map(([label, n]) => `${n} ${label}`).join(' · ');
    return { total, detail };
  }, [selectedExercices]);

  // Preview question (exerciseur / aperçu élève) + régénérateur « Autre
  // variante » fourni par ExerciseurCard (null pour les questions prof)
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [previewRegenerate, setPreviewRegenerate] = useState(null);
  const handlePreview = useCallback((question, regenerate = null) => {
    setPreviewQuestion(question);
    setPreviewRegenerate(() => regenerate);
  }, []);
  // Drawer panier (détail des exercices sélectionnés)
  const [showDrawer, setShowDrawer] = useState(false);
  // Mode groupes : où vont les exercices ajoutés depuis les exerciseurs
  // (« Commun » = le panier, donc les 3 groupes ; sinon un groupe seul).
  const [destination, setDestination] = useState('Commun');
  // Formulaire création question
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [, doPostQuestion] = usePostApi(`${url}banque-questions/`);
  // Édition persistée d'une question de banque (propriétaire seulement —
  // affichage gardé par BanqueQuestionCard, refus côté back sinon)
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [, doPutQuestion] = usePutApi(null, null);

  const { confirm, ConfirmDialog } = useConfirm();

  // Retour à la classe : la sélection en cours (panier) n'est pas conservée,
  // on demande confirmation dès qu'elle n'est pas vide.
  const handleRetourClasse = useCallback(async () => {
    const nbPropres = intention?.type === 'groupes' ? intention.groupes.reduce((n, g) => n + g.propres.length, 0) : 0;
    const nb = formatDetail.total + selectedIds.size + nbPropres;
    if (nb > 0) {
      const ok = await confirm({
        title: 'Quitter la composition ?',
        message: `${nb} exercice${nb > 1 ? 's' : ''} sélectionné${nb > 1 ? 's' : ''} : la sélection sera perdue si vous quittez maintenant.`,
        confirmLabel: 'Quitter sans lancer',
        variant: 'danger',
      });
      if (!ok) return;
    }
    navigate(`/home/creer/${idclasse}`);
  }, [formatDetail.total, selectedIds.size, intention, confirm, navigate, idclasse]);

  // Niveau & matière auto
  const nomRef = classeNom || groupNom || '';
  const niveau = NIVEAU_MAP[nomRef.charAt(0)] || '';
  const niveauChar = nomRef.charAt(0) || '';
  const matiere = user?.matiere || '';
  // Détour par un prérequis ouvert (bandeau, panier) : l'en-tête dit son
  // niveau et offre de revenir (ou d'aller à un autre attendu) ; le bandeau se tait (épreuve de
  // Musk, 2026-10-04 : composer un diagnostic depuis un prérequis prêtait à
  // confusion).
  const enDetour = !!attenduRetour && attenduRetour.id !== selectedAttendu?.id;
  const niveauDetour = enDetour && selectedAttendu?.niveau ? selectedAttendu.niveau.replace(/eme$/, 'e') : '';

  // Choix d'un attendu : affiché, panneau refermé, retenu pour la classe.
  const choisirAttendu = useCallback((att) => {
    setSelectedAttendu(att);
    setPanelOpen(false);
    setAttenduRetour(null);
    ecrireAttenduRetenu(idclasse, { ...att, niveauChar });
  }, [idclasse, niveauChar]);
  // Changement de classe dans la même page, ou mémoire d'un autre niveau que
  // celui de la classe (une fois le niveau connu) : on repart proprement.
  useEffect(() => {
    const retenu = lireAttenduRetenu(idclasse, niveau);
    const valide = !!retenu && (!niveauChar || !retenu.niveauChar || retenu.niveauChar === niveauChar);
    setSelectedAttendu((prev) => {
      if (!valide) return null;
      return prev && prev.id === retenu.id ? prev : retenu;
    });
    setPanelOpen(!valide);
  }, [idclasse, niveauChar, niveau]);

  const eleves = useMemo(() => {
    const src = classeDetail?.eleves;
    return Array.isArray(src) ? src : [];
  }, [classeDetail]);

  // Classe (ou groupe) sans élèves, une fois le détail réellement chargé :
  // seul le format papier a du sens — imprimer un sujet reste légitime,
  // lancer une passation non. Transmis au LaunchModal (availableFormats)
  // et à l'aperçu PDF (le panneau Évaluation de suivi s'y masque).
  const classeVide = !!classeDetail && eleves.length === 0;

  // Contexte transmis à l'aperçu PDF (type évaluation) : active le panneau
  // "Évaluation de suivi" qui POSTe /evaluations/ depuis l'onglet d'aperçu.
  const printContext = useMemo(() => ({
    idclasse,
    estClasse: !!classeNom,
    nomClasse: classeNom || groupNom || '',
    etablissementId: selectedEtablissementId,
    matiere: (matiere || 'maths').toLowerCase(),
    classeVide,
  }), [idclasse, classeNom, groupNom, selectedEtablissementId, matiere, classeVide]);

  // Hooks data
  const filters = {};
  if (niveau) filters.niveau = niveau;
  if (matiere) filters.matiere = matiere.toLowerCase();

  const { data: questionsClasse } = useBanqueQuestions(filters, refreshKey);
  // Attendu d'un autre niveau que la classe (2026-09-29) : ses questions et
  // exercices déjà générés, rangés ailleurs, s'ajoutent à la banque de la classe.
  const questionsAutresNiveaux = useQuestionsAttendusAutresNiveaux(selectedAttendu, niveau, refreshKey);
  const questions = useMemo(
    () => fusionnerQuestions(questionsClasse, questionsAutresNiveaux),
    [questionsClasse, questionsAutresNiveaux]
  );
  const sessionFilters = useMemo(() => {
    if (classeNom) return { classe: idclasse };
    if (groupNom) return { groupe: idclasse };
    return {};
  }, [classeNom, groupNom, idclasse]);
  // Sessions : conservées pour les bandeaux "en cours / programmées prêtes"
  // (les actions vivent dans Évaluations)
  const { data: sessions } = useSessionsQCM(sessionFilters, refreshKey);
  // Cartes — seule la liste sert encore ici : le LaunchModal en a besoin pour
  // le formulaire des équipes. Les assignations ne sont plus lues : elles sont
  // posées par le backend à la création de la session (voir perform_create).
  const { data: rawCartes } = useCartesAruco(refreshKey);
  const cartes = useMemo(() => Array.isArray(rawCartes) ? rawCartes : (rawCartes?.results || []), [rawCartes]);

  // Questions sélectionnées (objets complets)
  const selectedQuestions = useMemo(() => {
    const map = new Map(questions.map((q) => [q.id, q]));
    return [...selectedIds].map((id) => map.get(id)).filter(Boolean);
  }, [questions, selectedIds]);

  // Aplatissement UNIQUE de selectedExercices (Map du panier, groupée par
  // générateur) en liste plate d'exercices individuels. Chaque instance porte
  // ses deux identités :
  //   - _meta.exerciseurKey (clé Map) : clé de GROUPE pour l'impression — le
  //     print regroupe les instances consécutives d'un même exerciseur en un
  //     exo unique à sous-questions (cf. Print/helpers/groupQuestions.js) ;
  //   - _tmpId (clé Map + index) : INDIVIDUATION pour le flux session — permet
  //     au LaunchModal de lister/réordonner des items sans id DB, puis de
  //     raccorder chaque instance à l'id renvoyé par bulk-create au lancement.
  const generatedItems = useMemo(() => {
    const list = [];
    selectedExercices.forEach(({ payloads }, mapKey) => {
      payloads.forEach((p, i) => {
        list.push({
          ...p,
          _tmpId: `gen_${mapKey}_${i}`,
          _meta: { ...(p._meta || {}), exerciseurKey: mapKey },
        });
      });
    });
    return list;
  }, [selectedExercices]);

  // Tiroir du diagnostic, tiroir ouvert : d'abord une ligne par question
  // composée par TanoEd (clé stable par prérequis : le focus reste sur ↻), avec
  // sa question de rechange tirée une fois par question (null = ↻ grisé) ;
  // puis le reste (ajouts à la main, « Composer N »), marqué « ajoutée ».
  const rechanges = useRef(new WeakMap());
  const lignesDiagnostic = useMemo(() => {
    if (!enDiagnostic || !showDrawer) return null;
    const prerequis = [];
    const ajoutees = [];
    selectedExercices.forEach(({ payloads }, cle) => payloads.forEach((payload, i) => {
      const info = intention.infos[payload.attendu];
      if (!info || payload._meta?.origine !== 'auto' || payload._meta?.compoAuto) {
        ajoutees.push({ cle, ligne: `${cle}#${i}`, payload });
        return;
      }
      if (!rechanges.current.has(payload)) {
        rechanges.current.set(payload, autreQuestion(payload.attendu, { cle, payload }, intention.version, GENERATEURS));
      }
      prerequis.push({ cle, ligne: `prerequis-${payload.attendu}`, payload, prerequis: info, autre: rechanges.current.get(payload) });
    }));
    return { lignes: [...prerequis, ...ajoutees], nbPrerequis: prerequis.length };
  }, [enDiagnostic, showDrawer, intention, selectedExercices]);

  const remplacerQuestion = ({ cle, payload, autre }) => {
    const nouveau = versPanier(autre.attenduId, autre.payload, intention.infos);
    setSelectedExercices((prev) => remplacerDansPanier(prev, cle, payload, autre.cle, nouveau));
  };

  // Retire des instances précises d'une ligne du panier (la ligne part si elle est vide).
  const retirerDuPanier = (cle, lot) => {
    const reste = (selectedExercices.get(cle)?.payloads || []).filter((p) => !lot.includes(p));
    handleUpdateExercice(cle, 0, reste.length ? reste : null);
  };

  // Questions disponibles pour l'impression PDF : questions prof de la banque
  // + exercices TanoEd générés (chaque clic "+ Ajouter" a pushé un payload
  // frais, chaque version est distincte — nombres et distracteurs différents).
  const printableQuestions = useMemo(
    () => [...selectedQuestions, ...generatedItems],
    [selectedQuestions, generatedItems]
  );

  // R17 : un exercice qui ne peut pas devenir un QCM simple grise les cartes
  // QR, quelle que soit l'intention ; les autres sont adaptés au lancement.
  const nbSansQcm = useMemo(() => sansVersionQcm(printableQuestions, GENERATEURS).length, [printableQuestions]);
  const motifCartes = enGroupes ? MOTIF_CARTES_GROUPES
    : nbSansQcm > 0 ? `Cartes QR : ${pluriel(nbSansQcm, 'exercice')} sans version QCM` : null;
  // Le motif ne s'affiche pas sur une classe vide : son propre motif prime.
  const motifVisible = motifCartes && !classeVide;

  // ─── Intention (R2, R3, F4, F6) ───
  const propres = useMemo(
    () => (intention?.type === 'groupes' ? intention.groupes.flatMap((g) => g.propres) : []),
    [intention],
  );
  const nbPanier = formatDetail.total + selectedIds.size + propres.length;
  useEffect(() => {
    if (nbPanier === 0) setIntention(null);
  }, [nbPanier]);

  // Bilan de l'attendu affiché (F2) : une classe seulement, un groupe n'en a pas.
  // Seulement pour un attendu à prérequis actifs (réglage serveur
  // ATTENDUS_AVEC_ARBRE, 13/10/2026) : ailleurs, ni bandeau, ni diagnostic, ni
  // groupes (pas encore d'arbre validé ni d'indice de difficulté sur les cartes).
  const avecArbre = useAttendusAvecArbre();
  const bilan = useBilanCible(
    classeNom ? idclasse : null,
    prerequisActifs(avecArbre, selectedAttendu?.id) ? selectedAttendu.id : null,
  );

  // F6 : un bouton ✦ sur un panier non vide demande d'abord ; garder est le
  // choix par défaut. Résout vrai quand on peut démarrer.
  const confirmerRemplacement = useCallback(() => {
    if (nbPanier === 0) return Promise.resolve(true);
    return confirm({
      title: 'Remplacer votre session en cours ?',
      confirmLabel: 'Remplacer',
      cancelLabel: SESSIONS[intention?.type || 'evaluation'].garder,
    });
  }, [nbPanier, intention, confirm]);

  // F4, R5 : une question par prérequis bloquant, marquée ✦ ; nouvelle session.
  // `version` : CIBLE_CARTES (cartes QR) ou CIBLE_VARIE (papier, en ligne).
  const demarrerDiagnostic = useCallback(async (version) => {
    if (!bilan || !selectedAttendu) return;
    const cible = selectedAttendu;
    const { questions: composees, ignores } = composerDiagnostic(bilan, GENERATEURS, version);
    // Aucun prérequis n'a d'exerciseur : rien à composer, la session reste.
    if (composees.length === 0) return;
    if (!(await confirmerRemplacement())) return;
    const infos = infosDuBilan(bilan);
    const panier = new Map();
    // R5 amendée le 2026-10-04 : les bases d'abord, de la 6e à la 3e.
    ordonnerParNiveau(composees, infos).forEach(({ attenduId, cle, payload }) => {
      panier.set(cle, { payloads: [versPanier(attenduId, payload, infos)] });
    });
    setSelectedIds(new Set());
    setSelectedExercices(panier);
    setIntention({ type: 'diagnostic', cible, infos, ignores, version, parcours: parcoursDuBilan(bilan, cible, infos) });
    setAttenduRetour(null);
  }, [bilan, selectedAttendu, confirmerRemplacement]);

  // F5, R7, R8 : 3 groupes placés sur l'attendu (D) ou sur ses prérequis (C) ;
  // le noyau commun va au panier ; les prérequis de Fragile et les exercices en
  // plus de En cours et de Acquis leur sont propres. Nouvelle session.
  const demarrerGroupes = useCallback(async (base) => {
    if (!bilan || !selectedAttendu) return;
    const cible = selectedAttendu;
    if (!(await confirmerRemplacement())) return;
    diffusionRef.current = null;
    const placement = placerGroupes(bilan, base);
    const composes = composerGroupes(bilan, placement, GENERATEURS);
    const infos = infosDuBilan(bilan);
    const panier = new Map();
    composes[0].noyau.forEach(({ attenduId, cle, payload }) => {
      const prec = panier.get(cle)?.payloads || [];
      panier.set(cle, { payloads: [...prec, versPanier(attenduId, payload, infos)] });
    });
    setSelectedIds(new Set());
    setSelectedExercices(panier);
    setDestination('Commun');
    setIntention({
      type: 'groupes',
      base,
      cible,
      date: etatBandeau(bilan).date,
      manque: manqueTotal(composes),
      noms: Object.fromEntries((bilan.eleves || []).map((e) => [e.id, `${e.prenom || ''} ${e.nom || ''}`.trim() || `Élève ${e.id}`])),
      sansResultat: placement.sansResultat,
      infos,
      parcours: parcoursDuBilan(bilan, cible, infos),
      groupes: composes.map(({ nom, eleves, coupsDePouce, prerequis, propres, sansComplexe }) => ({
        nom,
        eleves,
        coupsDePouce,
        prerequis,
        sansComplexe,
        propres: propres.map(({ attenduId, cle, payload }) => exercicePropre(attenduId, cle, payload, infos)),
      })),
    });
    setAttenduRetour(null);
  }, [bilan, selectedAttendu, confirmerRemplacement]);

  // R9 : tout se modifie, groupe par groupe.
  const modifierGroupe = useCallback((nom, changer) => {
    setIntention((i) => (i?.type === 'groupes'
      ? { ...i, groupes: i.groupes.map((g) => (g.nom === nom ? changer(g) : g)) }
      : i));
  }, []);
  const deplacerEleve = useCallback((eleveId, vers) => {
    setIntention((i) => (i?.type === 'groupes'
      ? { ...i, groupes: i.groupes.map((g) => {
        const eleves = g.eleves.filter((e) => e !== eleveId);
        return { ...g, eleves: g.nom === vers ? [...eleves, eleveId] : eleves };
      }) }
      : i));
  }, []);
  const basculerAides = useCallback((nom) => modifierGroupe(nom, (g) => ({ ...g, coupsDePouce: !g.coupsDePouce })), [modifierGroupe]);
  const retirerPropre = useCallback((nom, id) => modifierGroupe(nom, (g) => ({ ...g, propres: g.propres.filter((p) => p.id !== id) })), [modifierGroupe]);
  // Lit la session dans la mise à jour : la fonction reste stable, les cartes
  // d'exerciseurs (memo) ne se redessinent pas à chaque retouche des groupes.
  const ajouterPropre = useCallback((nom, attenduId, cle, payload) => {
    setIntention((i) => {
      if (i?.type !== 'groupes') return i;
      const propre = exercicePropre(attenduId, cle, payload, { ...i.infos, [i.cible.id]: i.cible });
      return { ...i, groupes: i.groupes.map((g) => (g.nom === nom ? { ...g, propres: [...g.propres, propre] } : g)) };
    });
  }, []);
  const groupesAffiches = useMemo(() => {
    if (intention?.type !== 'groupes') return null;
    const sansResultat = new Set(intention.sansResultat);
    return intention.groupes.map((g) => ({ ...g, sansResultat: g.eleves.filter((e) => sansResultat.has(e)) }));
  }, [intention]);
  const nomEleve = useCallback((id) => intention?.noms?.[id] || `Élève ${id}`, [intention]);
  // F7 papier : un sujet par groupe non vide, sous un code neutre.
  const sujetsPapier = useMemo(() => {
    if (intention?.type !== 'groupes') return null;
    return decouperPapier(intention.groupes.map((g) => ({
      ...g,
      exercices: exercicesDuGroupe(g.propres, printableQuestions, intention.cible.id),
    }))).sujets.map((s) => ({ ...s, eleves: s.eleves.map(nomEleve) }));
  }, [intention, printableQuestions, nomEleve]);
  const nbElevesGroupes = groupesAffiches ? groupesAffiches.reduce((n, g) => n + g.eleves.length, 0) : 0;

  // « Annuler » : la session composée est vidée ; ce qu'elle a remplacé n'est
  // pas restauré (O7).
  const annulerComposition = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedExercices(new Map());
    setIntention(null);
    setShowDrawer(false);
  }, []);

  // « ouvrir » un prérequis : ses exercices, avec un retour vers l'attendu
  // cible (celui du diagnostic s'il y en a un). Un exercice ajouté depuis là
  // entre dans la session en cours, sans marque.
  const ouvrirAttendu = useCallback((att) => {
    if (!att) return;
    setAttenduRetour((r) => r || intention?.cible || selectedAttendu);
    setSelectedAttendu(att);
    setPanelOpen(false);
    setShowDrawer(false);
  }, [intention, selectedAttendu]);
  // Panneau des attendus : pendant une séance, un de ses prérequis s'ouvre en
  // détour (retour vers l'attendu de la séance, ajouts dans la séance).
  // Un attendu hors de l'arbre de la séance démarre une nouvelle session :
  // un attendu = une session, F6 d'abord (plan popup-choix-attendu, 2026-10-04).
  const choisirDuPanneau = useCallback(async (att) => {
    const prerequis = intention?.parcours?.find((a) => a.id === att.id && a.id !== intention.cible.id);
    if (prerequis) {
      ouvrirAttendu({ ...att, niveau: prerequis.niveau, description: prerequis.description || att.description });
      return;
    }
    if (intention && att.id !== intention.cible.id) {
      if (!(await confirmerRemplacement())) return;
      annulerComposition();
    }
    choisirAttendu(att);
  }, [intention, ouvrirAttendu, choisirAttendu, confirmerRemplacement, annulerComposition]);
  const ouvrirDuBilan = useCallback((id) => {
    const a = (bilan?.attendus || []).find((x) => x.attendu_id === id);
    if (a) ouvrirAttendu({ id, description: a.description, niveau: a.niveau, rubrique: a.rubrique });
  }, [bilan, ouvrirAttendu]);


  // Lancer en classe (ArUco) — la sélection est ADAPTÉE automatiquement avant
  // d'ouvrir la modale (utils/adaptationCartes.js) : les exercices générés
  // non-QCM sont remplacés par un QCM de la même carte, les autres laissés de
  // côté ; la modale dit ce qui a été fait et laisse annuler. Le panier, lui,
  // n'est pas touché.
  const [adaptation, setAdaptation] = useState(null);
  const handleLaunchEnClasse = useCallback(() => {
    setAdaptation(adapterPourCartes(printableQuestions, GENERATEURS));
    setLaunchMode('aruco');
    setShowLaunchModal(true);
  }, [printableQuestions]);

  // Lancer en ligne — tous types acceptés
  const handleLaunchEnLigne = useCallback(() => {
    setLaunchMode('en_ligne');
    setShowLaunchModal(true);
  }, []);

  // Éval papier — ouvre le LaunchModal directement sur le formulaire d'impression
  const handleLaunchPapier = useCallback(() => {
    setLaunchMode('papier');
    setShowLaunchModal(true);
  }, []);

  // Sessions actives (en_cours) — pour le bandeau rappel
  const sessionsActives = useMemo(() => {
    const all = Array.isArray(sessions) ? sessions : [];
    return all.filter((s) => s.statut === 'en_cours');
  }, [sessions]);

  // Sessions programmées prêtes (en_attente + date passée ou aujourd'hui)
  const sessionsProgrammeesPretes = useMemo(() => {
    const all = Array.isArray(sessions) ? sessions : [];
    const today = new Date().toISOString().slice(0, 10);
    return all.filter((s) => s.statut === 'en_attente' && s.date_passation && s.date_passation <= today);
  }, [sessions]);

  // Hooks POST
  const [, doPost] = usePostApi(null, null);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Rapport de suppression partielle du back (spec PROTECT) : les questions
  // déjà données à des élèves sont CONSERVÉES (`protected`), les autres
  // supprimées (`deleted`). Rétrocompatible : si le back ne renvoie pas
  // encore ce rapport, message vide → comportement actuel inchangé.
  const messageProteges = (data) => {
    const proteges = data?.protected;
    if (!Array.isArray(proteges) || proteges.length === 0) return '';
    const n = proteges.length;
    return `${n} question${n > 1 ? 's' : ''} conservée${n > 1 ? 's' : ''} : déjà donnée${n > 1 ? 's' : ''} à des élèves (l'historique de résultats est protégé).`;
  };

  // Suppression bulk de questions sélectionnées
  const handleDeleteSelected = useCallback(async () => {
    if (!selectedIds.size) return;
    const count = selectedIds.size;
    if (!(await confirm({ title: `Supprimer ${count} question${count > 1 ? 's' : ''} ?`, message: 'Cette action est irréversible. Les questions déjà données à des élèves seront conservées.', confirmLabel: 'Supprimer', variant: 'danger' }))) return;
    const result = await doPost({ ids: [...selectedIds] }, `${url}banque-questions/bulk-delete/`);
    if (result.success) {
      setSelectedIds(new Set());
      refresh();
      const info = messageProteges(result.data);
      if (info) alert(info);
    } else {
      const msg = result.error?.response?.data?.error || result.error?.response?.data?.detail || 'Erreur lors de la suppression.';
      alert(msg);
    }
  }, [selectedIds, doPost, refresh, confirm]);

  // Suppression d'UNE question de banque (depuis le ▾ « réutiliser » des
  // cartes exerciseur). Même sémantique de rapport partiel.
  const handleDeleteQuestion = useCallback(async (qId) => {
    if (!(await confirm({
      title: 'Supprimer cet exercice ?',
      message: 'Il ne sera plus proposé à la réutilisation. Action irréversible.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
    }))) return;
    const result = await doPost({ ids: [qId] }, `${url}banque-questions/bulk-delete/`);
    if (result.success) {
      setSelectedIds((prev) => {
        if (!prev.has(qId)) return prev;
        const next = new Set(prev);
        next.delete(qId);
        return next;
      });
      refresh();
      const info = messageProteges(result.data);
      if (info) alert('Cet exercice a déjà été donné à des élèves — il est conservé pour préserver leur historique de résultats.');
    } else {
      const msg = result.error?.response?.data?.error
        || result.error?.response?.data?.detail
        || 'Suppression impossible (exercice probablement utilisé dans une session passée).';
      alert(msg);
    }
  }, [doPost, refresh, confirm]);

  // Toggle sélection question
  const handleToggleSelect = useCallback((qId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  }, []);

  // Mise à jour d'un exercice généré — Map<"attId_catId_formatKey", { payloads: Array }>
  // Chaque clic "+ Ajouter" pousse un nouveau payload dans la liste ; le count
  // est dérivé via payloads.length (plus de désync possible).
  //
  // API : (key, newCount, payload)
  //   - newCount <= 0         → supprime l'entrée (bouton poubelle du drawer)
  //   - newCount > prev + 1   → gap > 1 : API conçue pour des increments unitaires,
  //                             warn console pour signaler un futur appel bulk
  //                             (cas pas réel aujourd'hui, à passer à payloads[]
  //                             si jamais).
  //   - payload = TABLEAU     → REMPLACE les instances de la clé par ce tableau
  //                             (vide → entrée supprimée). « Composer N » s'en
  //                             sert pour effacer la composition automatique
  //                             précédente en gardant les choix manuels
  //                             (2026-09-02).
  //   - sinon                 → append payload à payloads[]
  const handleUpdateExercice = useCallback((key, count, payload) => {
    setSelectedExercices(prev => {
      const next = new Map(prev);
      if (Array.isArray(payload)) {
        if (payload.length === 0) next.delete(key); else next.set(key, { payloads: payload });
        return next;
      }
      if (count <= 0) { next.delete(key); return next; }
      const prevPayloads = prev.get(key)?.payloads || [];
      if (count > prevPayloads.length + 1) {
        // eslint-disable-next-line no-console
        console.warn('[selectedExercices] API unitaire — gap +1 seulement. Pour du bulk, passer à payloads[].');
      }
      const newPayloads = payload ? [...prevPayloads, payload] : prevPayloads;
      next.set(key, { payloads: newPayloads });
      return next;
    });
  }, []);
  // Exerciseurs en mode groupes : un ajout à la main va au groupe choisi dans
  // « Ajouter à » ; « Commun », les retraits et toute composition (« Composer
  // N », marquée `compoAuto`) vont au panier.
  const majDepuisExerciseur = useCallback((key, count, payload) => {
    const ajoutManuel = payload && !Array.isArray(payload) && count > 0 && !payload._meta?.compoAuto;
    if (enGroupes && destination !== 'Commun' && ajoutManuel) {
      ajouterPropre(destination, payload.attendu ?? selectedAttendu?.id, key, payload);
      return;
    }
    handleUpdateExercice(key, count, payload);
  }, [enGroupes, destination, selectedAttendu, ajouterPropre, handleUpdateExercice]);

  // Créer une question personnalisée
  const handleCreateQuestion = useCallback(async (data) => {
    const payload = { ...data, etablissement: selectedEtablissementId };
    const result = await doPostQuestion(payload);
    if (result.success) {
      setShowCreateForm(false);
      refresh();
    }
  }, [doPostQuestion, selectedEtablissementId, refresh]);

  // Modifier une question de banque existante (même id, PUT)
  const handleUpdateQuestion = useCallback(async (data) => {
    if (!editingQuestion?.id) return;
    const payload = { ...data, etablissement: selectedEtablissementId };
    const result = await doPutQuestion(payload, `${url}banque-questions/${editingQuestion.id}/`);
    if (result.success) {
      setEditingQuestion(null);
      refresh();
    }
  }, [doPutQuestion, editingQuestion, selectedEtablissementId, refresh]);

  // ─── Handlers Sessions ───
  const extractErrorMsg = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    if (Array.isArray(data)) return data.join(' ');
    return data?.non_field_errors?.join(' ') || data?.titre?.join(' ') || data?.detail || 'Erreur lors de la sauvegarde.';
  };

  // Le titre automatique d'une séance, proposé dans les réglages du lancement :
  // le prof le garde ou le change (demande du prof, 2026-10-04).
  // Point médian, pas de tiret cadratin dans un texte visible (règle du projet).
  const titreAutomatique = useCallback((enLigne) => {
    const now = new Date();
    return `${enLigne ? 'Quizz en ligne' : 'QCM'} ${matiere} · ${now.toLocaleDateString('fr-FR')} ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  }, [matiere]);
  // Les exercices générés entrent en banque AVANT la séance : bulk-create
  // renvoie les questions créées avec leurs ids, dans l'ordre d'envoi. `ids`
  // (`_tmpId` → id) sert de cache : déjà connus, ils ne repartent pas.
  // Rend l'erreur, ou null.
  const persisterGeneres = async (questions, ids) => {
    const aPersister = questions.filter((q) => !q.id && !ids.has(q._tmpId));
    if (aPersister.length === 0) return null;
    const bulk = await doPost(
      { questions: aPersister.map(toBanquePayload), etablissement: selectedEtablissementId },
      `${url}banque-questions/bulk-create/`
    );
    if (!bulk?.success || !Array.isArray(bulk.data)) return bulk?.error ?? {};
    aPersister.forEach((q, i) => ids.set(q._tmpId, bulk.data[i].id));
    return null;
  };
  // Liste d'ids de la séance, dans l'ordre choisi dans la modale.
  const questionsSeance = (questions, ids) => questions.map((q, i) => ({
    banque_question_id: q.id ?? ids.get(q._tmpId),
    ordre: i + 1,
    temps_limite: 30,
  }));

  // F7 en ligne : une séance par groupe non vide, en série, ciblant les
  // élèves du groupe avec son réglage de coups de pouce (R16) ; durée et
  // correction communes. Le premier lancement fige la diffusion (groupes,
  // élèves, exercices, numéros de sujet, réglages) dans `diffusionRef` : une
  // relance après un échec envoie le reste de cette version, telle quelle,
  // sans recréer ni séance ni question déjà persistée. Une retouche faite
  // entre-temps ne peut donc ni priver un élève de séance ni lui en donner deux.
  const diffuserGroupesEnLigne = async (config, base) => {
    if (!diffusionRef.current) {
      diffusionRef.current = {
        base,
        groupes: groupesADiffuser(intention.groupes).map((g, i) => ({
          nom: g.nom,
          sujet: `Sujet ${i + 1}`,
          eleves: g.eleves,
          coupsDePouce: !!g.coupsDePouce,
          questions: exercicesDuGroupe(g.propres, config.questions, intention.cible.id),
        })),
        ids: new Map(),
        creees: new Set(),
      };
    }
    const d = diffusionRef.current;
    for (const g of d.groupes) {
      if (d.creees.has(g.nom)) continue;
      let erreur = await persisterGeneres(g.questions, d.ids);
      if (!erreur) {
        const resultat = await doPost({
          ...d.base,
          titre: `${d.base.titre} · ${g.sujet}`,
          coups_de_pouce_autorises: g.coupsDePouce,
          eleves_cibles: g.eleves,
          questions: questionsSeance(g.questions, d.ids),
        }, `${url}sessions-qcm/`);
        erreur = resultat?.success ? null : resultat?.error ?? {};
      }
      if (erreur) {
        const faits = d.groupes.filter((x) => d.creees.has(x.nom)).map((x) => x.sujet);
        setLaunchError(`${faits.length ? `${faits.length > 1 ? 'Séances' : 'Séance'} ${faits.join(', ')} ${faits.length > 1 ? 'créées' : 'créée'}. ` : ''}Échec pour le groupe ${g.nom} (${g.sujet}) : ${extractErrorMsg(erreur)} Relancez : les séances restantes partiront telles qu'au premier lancement (élèves, exercices, réglages), sans les retouches faites depuis.`);
        setLaunchLoading(false);
        refresh();
        return;
      }
      d.creees.add(g.nom);
    }
    diffusionRef.current = null;
    setShowLaunchModal(false);
    annulerComposition();
    setLaunchLoading(false);
    setSuccessInfo({ titre: `${d.base.titre} (${d.groupes.length} ${d.groupes.length > 1 ? 'séances' : 'séance'})`, ouverte: !!config.launchNow, nb: d.groupes.length });
    refresh();
  };

  const handleLaunch = async (config) => {
    setLaunchError(null);
    setLaunchLoading(true);

    const isEnLigne = config.mode_passation === 'en_ligne';
    // Le titre des réglages ; à défaut, le titre automatique.
    const titre = config.titre || titreAutomatique(isEnLigne);
    // Réglages communs à toutes les séances de ce lancement.
    const base = {
      titre,
      date_passation: config.datePassation || (isEnLigne ? new Date().toISOString().split('T')[0] : null),
      mode: config.mode,
      mode_passation: config.mode_passation || 'aruco',
      duree: config.dureeQuiz || 0,
      correction_visible: config.correctionVisible || false,
      coups_de_pouce_autorises: !!config.coupsDePouceAutorises,
      // Déroulé de fin de séance projetée — le LaunchModal ne remonte ces deux
      // réglages qu'en passation cartes, ensemble ou pas du tout : hors cartes
      // ils n'ont aucun écran où s'appliquer et le modèle garde ses défauts
      // (podium affiché, correction finale).
      ...(config.podiumActif === undefined
        ? {}
        : { podium_actif: config.podiumActif, mode_correction: config.modeCorrection }),
      classe: classeNom ? idclasse : null,
      groupe: groupNom ? idclasse : null,
      matiere: matiere.toLowerCase(),
      etablissement: selectedEtablissementId,
    };
    if (enGroupes && isEnLigne) {
      try {
        await diffuserGroupesEnLigne(config, base);
      } catch (err) {
        setLaunchError(extractErrorMsg(err));
        setLaunchLoading(false);
      }
      return;
    }

    const avecGeneres = config.questions.some((q) => !q.id);
    const idParTmp = new Map();
    const erreurBanque = await persisterGeneres(config.questions, idParTmp);
    if (erreurBanque) {
      setLaunchError(extractErrorMsg(erreurBanque));
      setLaunchLoading(false);
      return; // aucune session orpheline : la sélection reste intacte, on peut réessayer
    }
    const questionsPayload = questionsSeance(config.questions, idParTmp);
    const payload = { ...base, questions: questionsPayload };

    try {
      const result = await doPost(payload, `${url}sessions-qcm/`);
      if (!result?.success) {
        setLaunchError(extractErrorMsg(result?.error));
        setLaunchLoading(false);
        return;
      }

      const sessionId = result.data?.id;

      // Créer les équipes si mode équipe
      if (config.mode === 'equipe' && config.equipes.length > 0) {
        try {
          await Promise.all(config.equipes.map((eq) => axios.post(`${url}equipes-qcm/`, { ...eq, session: sessionId })));
        } catch (eqErr) {
          setLaunchError('Session créée mais erreur lors de la création des équipes.');
          setLaunchLoading(false);
          refresh();
          return;
        }
      }

      // « Donner aussi à » : une séance de plus par classe cochée, EN SÉRIE
      // (la préparation automatique des cartes lit et complète le stock du
      // prof : deux créations en parallèle pourraient fabriquer la même carte).
      // Mêmes questions partagées par défaut ; avec « nombres différents »,
      // les exercices générés sont retirés au sort à nouveau et persistés à
      // part pour chaque classe. Toujours en individuel (les équipes sont
      // propres à une classe). Un échec arrête la série : la principale et
      // les classes déjà faites restent, le message dit quoi relancer.
      const supplementaires = config.classesSupplementaires || [];
      // Classe ou groupe, comme la séance principale (groupes : 2026-09-30).
      const nomCourant = classeNom || groupNom;
      const cibleDe = (cl) => (classeNom ? { classe: cl.id, groupe: null } : { classe: null, groupe: cl.id });
      if (supplementaires.length > 0) {
        const faites = [];
        const etatDe = (cl, enCours) => (faites.includes(cl.id) ? 'ok' : cl.id === enCours?.id ? 'en_cours' : 'attente');
        for (const cl of supplementaires) {
          setAvancementClasses(supplementaires.map((c) => ({ ...c, etat: etatDe(c, cl) })));
          let questionsClasse = questionsPayload;
          if (config.nombresDifferents && avecGeneres) {
            const regenerees = regenererGeneres(config.questions, generateExercice);
            const idParTmpClasse = new Map();
            const erreurClasse = await persisterGeneres(regenerees, idParTmpClasse);
            if (erreurClasse) {
              setAvancementClasses(supplementaires.map((c) => ({ ...c, etat: c.id === cl.id ? 'echec' : etatDe(c, null) })));
              setLaunchError(`Séance créée pour ${nomCourant}${faites.length ? ` et ${faites.map((id) => supplementaires.find((c) => c.id === id)?.nom).join(', ')}` : ''}, mais pas pour ${cl.nom} : ${extractErrorMsg(erreurClasse)} Relancez-la depuis la composition.`);
              setLaunchLoading(false);
              refresh();
              return;
            }
            questionsClasse = questionsSeance(regenerees, idParTmpClasse);
          }
          const resultat = await doPost(
            { ...payload, mode: 'individuel', ...cibleDe(cl), questions: questionsClasse },
            `${url}sessions-qcm/`
          );
          if (!resultat?.success) {
            setAvancementClasses(supplementaires.map((c) => ({ ...c, etat: c.id === cl.id ? 'echec' : etatDe(c, null) })));
            setLaunchError(`Séance créée pour ${nomCourant}${faites.length ? ` et ${faites.map((id) => supplementaires.find((c) => c.id === id)?.nom).join(', ')}` : ''}, mais pas pour ${cl.nom} : ${extractErrorMsg(resultat?.error)} Relancez-la depuis la composition.`);
            setLaunchLoading(false);
            refresh();
            return;
          }
          faites.push(cl.id);
        }
        setAvancementClasses(supplementaires.map((c) => ({ ...c, etat: 'ok' })));
      }

      // Lancer maintenant ou programmer
      if (config.launchNow) {
        if (isEnLigne) {
          // Mode en ligne : la session est créée, on affiche le succès avec le code d'accès
          setShowLaunchModal(false);
          setSelectedIds(new Set());
          setSelectedExercices(new Map());
          setLaunchLoading(false);
          setSuccessInfo({ id: sessionId, titre, mode_passation: 'en_ligne', ouverte: true });
          refresh();
          return;
        }
        // Mode ArUco : le PC ne fait qu'OUVRIR LA PROJECTION. Ni `start` ni
        // `next_question` ne partent d'ici : la session reste `en_attente`
        // jusqu'à ce que le professeur appuie sur « Lancer le quizz » depuis
        // son smartphone (tout le pilotage vit sur le téléphone). Sans quoi
        // la classe s'installerait sur une session déjà démarrée à vide.
        setShowLaunchModal(false);
        setSelectedIds(new Set());
        setSelectedExercices(new Map());
        setLaunchLoading(false);
        refresh();
        navigate(`/home/qcm/${idclasse}/session/${sessionId}/projection`);
        return;
      }

      // Programmé : fermer et reset
      setShowLaunchModal(false);
      setSelectedIds(new Set());
      setSelectedExercices(new Map());
      setLaunchLoading(false);
      setSuccessInfo({ id: sessionId, titre });
      refresh();
    } catch (err) {
      setLaunchError(extractErrorMsg(err));
      setLaunchLoading(false);
    }
  };

  return (
    <div className="px-4 mt-5 max-w-full">
      {ConfirmDialog}
      <div className="">

        {/* ═══ EN-TÊTE UNIQUE, collant (allègement 2026-09-02) ═══
            Une seule couche avant les cartes : retour, classe, attendu courant
            avec « Changer d'attendu », et l'état des séances en badge à droite.
            Remplace le bandeau dégradé, les deux bandes (en cours, programmée)
            et la barre bleu nuit de l'attendu. Une seule couleur forte : le
            bleu marine des titres. Retour en contour orange léger (couleur
            des retours de l'application) qui ne domine plus la page. */}
        {/* Carte translucide « verre dépoli », comme les cartes de l'accueil :
            elle flotte sur le dégradé de l'application au lieu d'un aplat
            blanc (retour prof 2026-09-02). */}
        {/* Quatre plans (2026-09-03) : ce bloc de contexte en bleu glacier
            OPAQUE, bordure basse un peu plus marquée, pas d'ombre : il se
            détache du fond de travail bleu clair uni au lieu de s'y fondre
            (la version translucide sur dégradé donnait un ensemble lavé). */}
        {/* Pleine largeur (2026-09-03) : le bandeau sort des marges de la page
            (marges négatives), sans arrondi ni bordures latérales, mêmes
            retraits horizontaux que la barre du haut. Collant juste sous
            elle : 57 px = py-3 + avatar h-8 + bordure de TopBar. */}
        <div className="sticky top-[57px] z-40 -mx-4 -mt-5 px-6 lg:px-10 py-2.5 bg-[#F1F5FA] border-b-2 border-[#D7E8F8]">
          <div className="flex items-start gap-4">
            <button
              onClick={handleRetourClasse}
              className="shrink-0 mt-0.5 flex items-center gap-1 px-3 py-1.5 rounded-lg border border-orange-300 bg-white text-orange-600 text-xs font-semibold hover:bg-orange-50 transition"
            >
              <ChevronLeft size={15} />
              Retour à la classe
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-extrabold text-[#0f2a4a] tracking-tight leading-tight truncate">
                {classeNom ? classeNom : groupNom ? groupNom : 'QCM'}
                {matiere ? ` · ${matiere.charAt(0).toUpperCase()}${matiere.slice(1)}` : ''}
                {enDetour && (
                  <span className="ml-2 align-middle text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    Prérequis{niveauDetour ? ` de ${niveauDetour}` : ''}
                  </span>
                )}
              </h1>
              {selectedAttendu ? (
                <div className="mt-0.5 flex items-end gap-3">
                  <div className="flex-1 min-w-0">
                    {selectedAttendu.descName ? (
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold truncate">{selectedAttendu.descName}</p>
                    ) : null}
                    <p className="text-[15px] font-bold text-[#0f2a4a] truncate">
                      {modeRevue && (
                        <span className="font-mono text-[11px] font-bold px-1.5 py-[1px] mr-1.5 rounded-md bg-blue-50 text-blue-600 border border-blue-100 align-middle">{selectedAttendu.id}</span>
                      )}
                      {/* « 6e » : attendu pris dans un autre niveau que la classe (2026-09-29). */}
                      {horsNiveau(selectedAttendu, niveauChar) ? (
                        <span className="mr-1.5 align-middle"><PastilleNiveau attendu={selectedAttendu} niveauClasse={niveauChar} /></span>
                      ) : null}
                      <MathText>{selectedAttendu.description}</MathText>
                    </p>
                  </div>
                  {enDetour && (
                    // Passer d'un prérequis à l'autre sans revenir d'abord
                    // (2026-10-04) : le panneau les liste en tête pendant une séance.
                    <button
                      type="button"
                      onClick={() => setPanelOpen(true)}
                      className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-lg border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 transition"
                    >
                      <Search size={14} />
                      Changer d'attendu
                    </button>
                  )}
                  {enDetour ? (
                    <button
                      type="button"
                      onClick={() => choisirAttendu(attenduRetour)}
                      title={attenduRetour.description}
                      aria-label={`Revenir à ${texteLisible(attenduRetour.description)}`}
                      className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-600/20 transition"
                    >
                      <ChevronLeft size={14} />
                      Revenir à l'attendu de départ
                    </button>
                  ) : (
                    <button
                      onClick={() => setPanelOpen(true)}
                      className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-600/20 transition"
                    >
                      <Search size={14} />
                      Changer d'attendu
                    </button>
                  )}
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-500">Choisissez ce que vous voulez travailler.</p>
              )}
            </div>
            {/* État des séances de la classe, en badges (plus de bandes), et
                l'interrupteur discret du mode revue. */}
            <div className="shrink-0 flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={basculerModeRevue}
                aria-pressed={modeRevue}
                title="Afficher les numéros d'attendu et les clés de format (revue des exerciseurs)"
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition ${
                  modeRevue ? 'bg-slate-700 text-white border-slate-700' : 'bg-white/60 text-slate-400 border-slate-200 hover:text-slate-600'
                }`}
              >
                <Code size={11} />
                Mode revue
              </button>
            {(sessionsActives.length > 0 || sessionsProgrammeesPretes.length > 0) && (
              <div className="flex flex-col items-end gap-1">
                {sessionsActives.length > 0 && (
                  <button
                    onClick={() => navigate(`/home/evaluations/${idclasse}`)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition"
                  >
                    <Play size={11} className="animate-pulse" />
                    {sessionsActives.length} évaluation{sessionsActives.length > 1 ? 's' : ''} en cours · Reprendre →
                  </button>
                )}
                {sessionsProgrammeesPretes.length > 0 && (
                  <button
                    onClick={() => navigate(`/home/evaluations/${idclasse}`)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition"
                  >
                    <Clock size={11} />
                    {sessionsProgrammeesPretes.length} programmée{sessionsProgrammeesPretes.length > 1 ? 's' : ''} prête{sessionsProgrammeesPretes.length > 1 ? 's' : ''} · Lancer →
                  </button>
                )}
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="mt-5 space-y-4">

          {/* Pastille succès création, dans une zone toujours présente pour
              que son arrivée soit annoncée (la zone « status » est plus bas). */}
          <div aria-live="polite">
          {successInfo && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
              <CheckCircle size={16} className="shrink-0" />
              {/* Selon le lancement : une séance en ligne lancée maintenant est déjà
                  ouverte aux élèves ; une séance programmée attend sa date. */}
              <span className="font-medium">
                {successInfo.ouverte
                  ? `${successInfo.nb > 1 ? 'Séances ouvertes' : 'Séance ouverte'} : « ${successInfo.titre} », ${successInfo.nb > 1 ? 'chaque élève trouve la sienne dans son espace' : 'les élèves la trouvent dans leur espace'}`
                  : `QCM « ${successInfo.titre} » programmé : retrouvez-le dans Évaluations`}
              </span>
              <button
                onClick={() => { setSuccessInfo(null); navigate(`/home/evaluations/${idclasse}`); }}
                className="ml-auto text-xs text-emerald-500 hover:text-emerald-700 font-medium transition"
              >
                Voir dans Évaluations →
              </button>
              <button
                onClick={() => setSuccessInfo(null)}
                aria-label="Fermer"
                className="text-emerald-300 hover:text-emerald-500 transition"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          )}
          </div>

          {/* P3 : ce que TanoEd a composé se voit, et s'annule. Zone
              d'annonce toujours présente, pour que l'arrivée du message soit
              lue par les lecteurs d'écran. */}
          <div role="status">
            {(intention?.type === 'diagnostic' || intention?.type === 'groupes') && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800">
                <Sparkles size={15} className="shrink-0" />
                {intention.type === 'diagnostic' ? (
                  <>
                    <span>TanoEd a composé le diagnostic : 1 question par prérequis indispensable. Tout est modifiable.</span>
                    {intention.ignores.length > 0 && (
                      <span className="text-blue-600">{pluriel(intention.ignores.length, 'prérequis')} sans exercice TanoEd, ignoré{intention.ignores.length > 1 ? 's' : ''}.</span>
                    )}
                  </>
                ) : (
                  <>
                    <span>
                      TanoEd a préparé 3 groupes d'après la mesure du {formatJourMois(intention.date)} {intention.base === 'prerequis' ? 'des prérequis' : "de l'attendu"}.
                      {' '}Élèves, exercices et coups de pouce sont modifiables.
                    </span>
                    {intention.manque > 0 && (
                      <span className="text-blue-600">{intention.manque} exercice{intention.manque > 1 ? 's' : ''} de l'attendu {intention.manque > 1 ? "n'ont pas pu être composés" : "n'a pas pu être composé"}.</span>
                    )}
                  </>
                )}
                <span aria-hidden="true">·</span>
                <button type="button" onClick={annulerComposition} className="font-bold underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded">
                  Annuler
                </button>
              </div>
            )}
          </div>

          {groupesAffiches && (
            <>
              <GroupesBesoin
                groupes={groupesAffiches}
                base={intention.base}
                nbCommuns={printableQuestions.length}
                nomEleve={nomEleve}
                onDeplacer={deplacerEleve}
                onBasculerAides={basculerAides}
              />
              <div role="group" aria-label="Ajouter les exercices des exerciseurs à" className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-gray-600">Ajouter à :</span>
                {['Commun', ...intention.groupes.map((g) => g.nom)].map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={destination === d}
                    onClick={() => setDestination(d)}
                    className={`px-2 py-0.5 rounded-full border focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                      destination === d ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {d === 'Commun' ? 'Commun aux 3 groupes' : d}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Contenu : exercices de l'attendu sélectionné OU empty state */}
          {selectedAttendu ? (
            <div className="space-y-4">
              {/* L'attendu courant et « Changer d'attendu » vivent dans
                  l'en-tête collant ci-dessus. Le bandeau (F2) ne cache
                  jamais les exerciseurs. */}
              {!enDetour && (
                <BandeauAttendu
                  bilan={bilan}
                  groupesEnCours={enGroupes && intention.cible?.id === selectedAttendu?.id}
                  sessionAilleurs={(enGroupes || enDiagnostic) && intention.cible && intention.cible.id !== selectedAttendu?.id ? intention : null}
                  onComposerDiagnostic={demarrerDiagnostic}
                  onOuvrir={ouvrirDuBilan}
                  onPreparerGroupes={demarrerGroupes}
                  onRevenirSession={() => choisirAttendu(intention.cible)}
                />
              )}
              <AttenduExercices
              attenduId={selectedAttendu.id}
              rubrique={selectedAttendu.rubrique}
              description={selectedAttendu.description}
              themeName={selectedAttendu.themeName}
              descName={selectedAttendu.descName}
              questions={questions}
              niveau={niveau}
              matiere={matiere}
              niveauChar={niveauChar}
              niveauAttendu={selectedAttendu.niveau}
              modeRevue={modeRevue}
              selectable
              selectedExercices={selectedExercices}
              onUpdateExercice={majDepuisExerciseur}
              destination={enGroupes && destination !== 'Commun' ? destination : null}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onPreview={handlePreview}
              onCreateQuestion={() => setShowCreateForm(true)}
              onEditQuestion={setEditingQuestion}
              onDeleteQuestion={handleDeleteQuestion}
            />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              {/* Sur le fond bleu clair de l'écran, le gris clair ne se lisait
                  pas : encre foncée et graisse (retour prof 2026-09-02). */}
              <BookOpen size={48} className="text-blue-500 mb-4" />
              <h3 className="text-xl font-extrabold text-slate-800 mb-2">Composez votre session</h3>
              <p className="text-[15px] font-semibold text-slate-600 mb-6 max-w-md leading-relaxed">
                Choisissez ce que vous voulez travailler. Les exercices de l'attendu s'afficheront ici, prêts à être ajoutés.
              </p>
              {!panelOpen && (
                <button
                  onClick={() => setPanelOpen(true)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <Search size={16} /> Que voulez-vous travailler ?
                </button>
              )}
            </div>
          )}

          {/* Panel overlay arborescence */}
          <PanelAttendus
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            niveauChar={niveauChar}
            refreshKey={refreshKey}
            questions={questions}
            selectedAttenduId={selectedAttendu?.id}
            attenduCourant={intention?.cible || attenduRetour || selectedAttendu}
            onSelectAttendu={choisirDuPanneau}
            seance={intention ? { titre: enGroupes ? 'Groupes de besoin en cours' : 'Diagnostic en cours' } : null}
            modeRevue={modeRevue}
          />

          {/* La VUE HISTORIQUE a été supprimée : la consultation ET toutes
              les actions sessions (Lancer/Reprendre/Modifier/Noter/Équipes/
              correction/Supprimer) vivent dans la liste unifiée Évaluations. */}
        </div>
      </div>

      {/* ═══ STICKY SELECTION BAR + DRAWER PANIER ═══ */}
      {nbPanier > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col items-center pointer-events-none">

          {/* Drawer panier — slide-up quand ouvert */}
          {showDrawer && (
            <div className="pointer-events-auto w-full max-w-2xl mx-4 mb-2 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden" style={{ animation: 'slideUp 0.2s ease-out' }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                {lignesDiagnostic ? (
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Votre diagnostic</span>
                    <span className="text-gray-500"> · {lignesDiagnostic.nbPrerequis} prérequis, une question chacun · </span>
                    <span className="inline-flex items-center gap-0.5 text-blue-600"><Sparkles size={11} />proposé par TanoEd</span>
                  </span>
                ) : (
                  <span className="text-sm font-semibold text-gray-700">Votre session</span>
                )}
                <button onClick={() => setShowDrawer(false)} className="text-xs text-gray-400 hover:text-gray-600 transition">Fermer</button>
              </div>
              <div className="max-h-[40vh] overflow-auto divide-y divide-gray-50">
                {enGroupes && <h4 className="px-4 pt-2 text-xs font-bold text-gray-700">Commun aux 3 groupes</h4>}
                {lignesDiagnostic?.lignes.map(({ cle, ligne, payload, prerequis, autre }) => (
                  <LigneDiagnostic
                    key={ligne}
                    prerequis={prerequis}
                    payload={payload}
                    nom={apercuTexte(payload)}
                    onOuvrir={() => ouvrirAttendu(prerequis)}
                    onAutre={autre ? () => remplacerQuestion({ cle, payload, autre }) : undefined}
                    onApercu={() => handlePreview(payload)}
                    onRetirer={() => retirerDuPanier(cle, [payload])}
                  />
                ))}
                {/* Exercices TanoEd */}
                {/* En mode groupes, une ligne par exercice : chacun s'ouvre et se
                    retire seul (le noyau tire souvent 3 fois la même carte). */}
                {!lignesDiagnostic && [...selectedExercices.entries()].flatMap(([key, { payloads }]) => (enGroupes
                  ? payloads.map((p, i) => ({ cle: key, ligne: `${key}#${i}`, lot: [p], rang: payloads.length > 1 ? `, ${i + 1} sur ${payloads.length}` : '' }))
                  : [{ cle: key, ligne: key, lot: payloads, rang: '' }]
                )).map(({ cle: key, ligne, lot, rang }) => {
                  const count = lot.length;
                  const payload = lot[0];
                  if (!payload) return null;
                  const label = getQuestionTypeLabel(payload);
                  const desc = apercuTexte(payload);
                  // F8 : marque des exercices composés par TanoEd.
                  const nbProposes = lot.filter((p) => p._meta?.compoAuto || p._meta?.origine === 'auto').length;
                  return (
                    <div key={ligne} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className="px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-[10px] font-semibold flex-shrink-0">{label}</span>
                      {nbProposes > 0 && <MarqueProposee nb={nbProposes === count ? null : nbProposes} />}
                      <span className="flex-1 min-w-0">
                        <ApercuExercice payload={payload} className="text-gray-600" />
                      </span>
                      <IconesModes payload={payload} />
                      {count > 1 && <span className="text-xs font-semibold text-gray-500">×{count}</span>}
                      <ActionsLigne
                        nom={`${desc}${rang}`}
                        onApercu={() => handlePreview(payload)}
                        onRetirer={() => retirerDuPanier(key, lot)}
                      />
                    </div>
                  );
                })}
                {/* Questions banque : « Prof » (créées main) ou « Exerciseur »
                    (instances générées réutilisées, generator_key non nul) */}
                {[...selectedIds].map(qId => {
                  const q = questions.find(item => item.id === qId);
                  if (!q) return null;
                  const desc = q.texte?.replace(/<[^>]*>/g, '').slice(0, 60) || 'Question';
                  const estGenere = Boolean(q.generator_key);
                  if (lignesDiagnostic) {
                    return <LigneDiagnostic key={qId} payload={q} nom={apercuTexte(q)} onApercu={() => handlePreview(q)} onRetirer={() => handleToggleSelect(qId)} />;
                  }
                  return (
                    <div key={qId} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold flex-shrink-0 ${
                        estGenere ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {estGenere ? 'Exerciseur' : 'Prof'}
                      </span>
                      <span className="flex-1 text-gray-600 truncate">{desc}</span>
                      <IconesModes payload={q} />
                      <ActionsLigne nom={desc} onApercu={() => handlePreview(q)} onRetirer={() => handleToggleSelect(qId)} />
                    </div>
                  );
                })}
                {enGroupes && (
                  <PanierGroupes
                    groupes={intention.groupes}
                    onApercu={handlePreview}
                    onRetirer={retirerPropre}
                  />
                )}
              </div>
            </div>
          )}

          {/* Sticky bar */}
          <div className="p-4 w-full flex flex-col items-center gap-1">
            <div
              key={nbPanier}
              className="pointer-events-auto flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 rounded-2xl bg-white border border-gray-200 shadow-2xl"
              style={{ animation: 'badgePulse 0.25s ease-out' }}
            >
              {/* Détail cliquable — ouvre le drawer */}
              <button
                onClick={() => setShowDrawer(prev => !prev)}
                className="text-xs sm:text-sm text-gray-700 flex items-center gap-1.5 flex-wrap hover:text-blue-600 transition"
              >
                {/* R2 : l'intention se lit en permanence. */}
                {intention?.type === 'diagnostic' ? (
                  <span className="flex items-center gap-1.5 min-w-0 text-blue-800">
                    <Sparkles size={14} className="shrink-0" />
                    <strong>Diagnostic</strong>
                    <span>·</span>
                    <span className="truncate max-w-[14rem]">{intention.cible.description}</span>
                    <span>·</span>
                    <strong>{pluriel(nbPanier, 'question')}</strong>
                  </span>
                ) : intention?.type === 'groupes' ? (
                  <span className="flex items-center gap-1.5 min-w-0 text-blue-800">
                    <Sparkles size={14} className="shrink-0" />
                    <strong>Groupes de besoin</strong>
                    <span>·</span>
                    <span className="truncate max-w-[14rem]">{intention.cible.description}</span>
                    <span>·</span>
                    <span>{intention.base === 'prerequis' ? 'sur les prérequis' : "sur l'attendu"}</span>
                    <span>·</span>
                    <span>3 groupes</span>
                    <span>·</span>
                    <strong>{pluriel(nbElevesGroupes, 'élève')}</strong>
                  </span>
                ) : (
                  <>
                    <span className="font-semibold text-[#0f2a4a]">Évaluation ·</span>
                    {formatDetail.total > 0 && (
                      <strong>{pluriel(formatDetail.total, 'exercice')}</strong>
                    )}
                    {formatDetail.total > 0 && formatDetail.detail && (
                      <span className="text-gray-400 hidden sm:inline">({formatDetail.detail})</span>
                    )}
                    {formatDetail.total > 0 && selectedIds.size > 0 && (
                      <span className="text-gray-300">·</span>
                    )}
                    {selectedIds.size > 0 && (
                      <strong className="text-[#0f2a4a]">{pluriel(selectedIds.size, 'question')} du prof</strong>
                    )}
                  </>
                )}
              </button>
              <button
                onClick={annulerComposition}
                className="text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 transition"
              >
                Tout retirer
              </button>
              {/* Terminal de lancement (2026-09-03) : trois destinations au même
                  rang, chacune sa couleur (indigo cartes, turquoise en ligne,
                  ambre papier, reprises par IconesModes) et un verbe d'action.
                  Lancer avec les cartes — QCM simples uniquement (banque + exerciseur).
                  Classe vide : passation impossible, bouton désactivé à la
                  SOURCE (le modal restreint aussi, ceinture et bretelles). */}
              <button
                onClick={handleLaunchEnClasse}
                disabled={Boolean(motifCartes) || classeVide}
                title={classeVide
                  ? "Classe sans élèves : importez les élèves pour lancer une passation"
                  : motifCartes || undefined}
                aria-describedby={motifVisible ? 'motif-cartes' : undefined}
                className={`relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-white font-semibold text-xs sm:text-sm shadow-md transition ${
                  (motifCartes || classeVide)
                    ? 'bg-indigo-300 cursor-not-allowed'
                    : 'bg-indigo-500 hover:bg-indigo-600 shadow-indigo-500/20'
                }`}
              >
                <ScanLine size={15} />
                <span className="hidden sm:inline">Lancer avec les cartes</span>
                <span className="sm:hidden">Cartes</span>
              </button>
              {/* En ligne — désactivé aussi sur classe vide */}
              <button
                onClick={handleLaunchEnLigne}
                disabled={classeVide}
                title={classeVide ? "Classe sans élèves : importez les élèves pour lancer une passation" : undefined}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-white font-semibold text-xs sm:text-sm shadow-md transition ${
                  classeVide
                    ? 'bg-teal-300 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/20'
                }`}
              >
                <Monitor size={15} />
                <span className="hidden sm:inline">Faire en ligne</span>
                <span className="sm:hidden">En ligne</span>
              </button>
              {/* Éval papier */}
              <button
                onClick={handleLaunchPapier}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-xs sm:text-sm shadow-md shadow-amber-500/20 transition"
              >
                <Printer size={15} />
                <span className="hidden sm:inline">Générer l'éval papier</span>
                <span className="sm:hidden">Papier</span>
              </button>
              {/* Supprimer — questions du prof uniquement */}
              {selectedIds.size > 0 && (
                <>
                  <div className="w-px h-5 bg-gray-200 ml-1" />
                  <button
                    onClick={handleDeleteSelected}
                    className="p-2 rounded-lg text-red-300 hover:text-red-500 hover:bg-red-50 transition"
                    title="Supprimer la sélection"
                  >
                    <Trash2 size={15} />
                  </button>
                </>
              )}
            </div>
            {motifVisible && (
              <p id="motif-cartes" className="pointer-events-auto px-2.5 py-0.5 rounded-full bg-white/95 border border-indigo-100 text-[11px] font-medium text-indigo-700">
                {motifCartes}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ═══ PREVIEW QUESTION MODAL (aperçu élève interactif) ═══ */}
      {previewQuestion && (
        <ApercuEleveModal
          question={previewQuestion}
          regenerate={previewRegenerate}
          onClose={() => { setPreviewQuestion(null); setPreviewRegenerate(null); }}
        />
      )}

      {/* ═══ CREATE QUESTION MODAL ═══ */}
      {showCreateForm && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowCreateForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
            <QuestionForm
              question={selectedAttendu ? { attendu: selectedAttendu.id } : null}
              niveau={niveau}
              matiere={matiere}
              niveauChar={niveauChar}
              descripteurContext={selectedAttendu ? { themeName: selectedAttendu.themeName, descName: selectedAttendu.descName, attendus: [{ id: selectedAttendu.id, description: selectedAttendu.description }] } : null}
              onSubmit={handleCreateQuestion}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      )}

      {/* ═══ EDIT QUESTION MODAL (propriétaire) ═══ */}
      {editingQuestion && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditingQuestion(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
            <QuestionForm
              question={editingQuestion}
              niveau={niveau}
              matiere={matiere}
              niveauChar={niveauChar}
              descripteurContext={selectedAttendu ? { themeName: selectedAttendu.themeName, descName: selectedAttendu.descName, attendus: [{ id: selectedAttendu.id, description: selectedAttendu.description }] } : null}
              onSubmit={handleUpdateQuestion}
              onCancel={() => setEditingQuestion(null)}
            />
          </div>
        </div>
      )}

      {/* ═══ LAUNCH MODAL ═══ */}
      {showLaunchModal && (
        <LaunchModal
          launchMode={launchMode}
          selectedQuestions={launchMode === 'aruco' && adaptation ? adaptation.questions : printableQuestions}
          adaptation={launchMode === 'aruco' ? adaptation : null}
          autresClasses={enGroupes ? AUCUNE_CLASSE : autresClasses}
          typeCibles={typeCibles}
          avancementClasses={avancementClasses}
          printContext={printContext}
          availableFormats={classeVide ? FORMATS_CLASSE_VIDE : enGroupes ? FORMATS_GROUPES : undefined}
          coupsDePouceParSeance={!enGroupes}
          motifCartes={motifCartes}
          sujets={sujetsPapier}
          titreAuto={titreAutomatique}
          cartes={cartes}
          eleves={eleves}
          isLoading={launchLoading}
          error={launchError}
          onLaunch={handleLaunch}
          onClose={() => { setShowLaunchModal(false); setLaunchError(null); setLaunchMode(null); setAdaptation(null); setAvancementClasses(null); }}
        />
      )}

    </div>
  );
};

export default QCMDashboard;
